import type { FieldDelta, RowComparison, WeekRow } from "./weekDelta";
import { nextWeekPairs } from "./nextWeekProposal";
import type { SetFieldWrite } from "./setFieldWrite";

/* THE PROPOSALS OVERLAY — W72 step 2 (Proposal 2 + his amendment: "just an addon which can toggle on/off
 * … with its numbers or whatever it proposed", Fable's rulings 2026-09-25).
 *
 * 🔴 THE MODEL: next week is ALREADY WRITTEN — the phone's `CoachNextWeekService` writes the copy with the
 * progressed numbers — so the overlay shows moves that EXIST. The three actions follow from that:
 *   • `Keep`         — writes NOTHING (a mark for this visit only; the number is already there).
 *   • `Change…`      — opens the existing set editor on that exercise (the coach's own value).
 *   • `Hold at 60`   — writes THIS week's value back into next week's rows for that ONE field, on exactly the
 *                      set rows that row groups, then re-reads from the server (no local shadow).
 * The words are the coach's (`Next week 62.5 · was 60`), and the number is IN the Hold button, so the write
 * is legible before it happens. "accept / adjust / hold" are never labels.
 *
 * ⚠️ A CHANGE IN THE NUMBER OF SETS IS NOT HELD FROM HERE: putting a set back or taking one away is an
 * insert or a delete of set rows, which `Change…` (the editor) owns. The row says so instead of offering a
 * button that could not keep its promise.
 */

/* QUEUE N2d (2026-10-06): the sentence used to end "— the phone's Clients tab writes it", which was false: the only writer
 * is the phone's automatic switch (`CoachAutoApplyRunner`, default OFF). The fact alone; the review page offers the act. */
export const NO_NEXT_WEEK_LINE = "Nothing set for next week yet.";

/** `?proposals=on|off` pins the overlay for a still or a link; otherwise the coach's stored choice. */
export function overlayOn(stored: boolean, q: Record<string, string>): boolean {
  if (q["proposals"] === "on") return true;
  if (q["proposals"] === "off") return false;
  return stored;
}

function num(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

/** One field's value as the coach reads it: `62.5 kg`, `8 reps`, `30 s`, `4 sets`; absent is `—`. */
export function fieldValue(field: FieldDelta["field"], v: number | null): string {
  if (v === null) return "—";
  switch (field) {
    case "weightKg": return `${num(v)} kg`;
    case "reps": return `${num(v)} ${v === 1 ? "rep" : "reps"}`;
    case "holdSeconds": return `${num(v)} s`;
    case "sets": return `${num(v)} ${v === 1 ? "set" : "sets"}`;
  }
}

/** The row's sentence: `Next week 62.5 kg · was 60 kg`. */
export function proposalLine(d: FieldDelta): string {
  return `Next week ${fieldValue(d.field, d.to)} · was ${fieldValue(d.field, d.from)}`;
}

/** Whether `Hold at …` can keep its promise on this row: a per-set field, a value to go back to, rows to write. */
export function canHold(d: FieldDelta, row: Pick<WeekRow, "setIds">): boolean {
  return d.field !== "sets" && d.from !== null && (row.setIds?.length ?? 0) > 0;
}

/** `Hold at 60` — the value going back, in the button (the row's line carries the unit). */
export function holdLabel(d: FieldDelta): string {
  return `Hold at ${d.from === null ? "—" : num(d.from)}`;
}

/**
 * ONE MOVE, IN PARTS, so the row can put ONLY `→ 62.5` in the accent (Fable, step 2 FIX 2: "the smallest
 * possible mark"): `60` · `62.5` · `kg`, read as `60 → 62.5 kg`. The unit follows the NEW value.
 */
export function deltaParts(d: FieldDelta): { from: string; to: string; unit: string } {
  const unit = (v: number | null) => {
    switch (d.field) {
      case "weightKg": return "kg";
      case "holdSeconds": return "s";
      case "reps": return v === 1 ? "rep" : "reps";
      case "sets": return v === 1 ? "set" : "sets";
    }
  };
  return { from: d.from === null ? "—" : num(d.from), to: d.to === null ? "—" : num(d.to), unit: unit(d.to) };
}

/**
 * The open overlay, ONE LINE PER EXERCISE (FIX 2 + 3): a moved exercise gathers every move across its set
 * groups; an added or dropped one is a quiet line; the unchanged ones are only COUNTED (`+ 9 unchanged`),
 * never listed — a list of set groups is the set table leaking into a calendar cell.
 */
export type MovedExercise = {
  key: string;
  exerciseId: string;
  exerciseRowId: string | undefined;
  moves: { delta: FieldDelta; row: WeekRow }[];
  /** Rows of THIS exercise that next week adds / removes, said on the SAME line (`+1 row next week`). */
  addedRows: number;
  droppedRows: number;
};
/**
 * 🔴 ONE LINE PER EXERCISE, keyed on the exercise alone (Fable, step 2: "never a second line for the same name").
 * An exercise that keeps rows AND gains or loses one reads `2 → 1 set · 8 → 9 reps · +1 row next week`; only an
 * exercise with NO row on the other side is `new next week` / `not in next week`; one whose every row is
 * unchanged is only counted.
 */
export function groupMoves(comparisons: readonly RowComparison[]): {
  moved: MovedExercise[]; added: string[]; dropped: string[]; unchanged: number;
} {
  type Acc = { exerciseRowId: string | undefined; moves: { delta: FieldDelta; row: WeekRow }[]; kept: number; addedRows: number; droppedRows: number };
  const byExercise = new Map<string, Acc>();
  for (const c of comparisons) {
    const id = c.row.exerciseId;
    const a = byExercise.get(id) ?? { exerciseRowId: undefined, moves: [], kept: 0, addedRows: 0, droppedRows: 0 };
    if (c.kind === "new") a.addedRows += 1;
    else if (c.kind === "dropped") a.droppedRows += 1;
    else {
      a.kept += 1;
      a.exerciseRowId ??= c.row.exerciseRowId;
      for (const d of c.deltas) a.moves.push({ delta: d, row: c.row });
    }
    byExercise.set(id, a);
  }
  const moved: MovedExercise[] = [];
  const added: string[] = [];
  const dropped: string[] = [];
  let unchanged = 0;
  for (const [id, a] of byExercise) {
    if (a.kept === 0 && a.addedRows > 0 && a.droppedRows === 0) { added.push(id); continue; }
    if (a.kept === 0 && a.droppedRows > 0 && a.addedRows === 0) { dropped.push(id); continue; }
    if (a.moves.length === 0 && a.addedRows === 0 && a.droppedRows === 0) { unchanged += 1; continue; }
    moved.push({ key: id, exerciseId: id, exerciseRowId: a.exerciseRowId, moves: a.moves, addedRows: a.addedRows, droppedRows: a.droppedRows });
  }
  return { moved, added, dropped, unchanged };
}

/** The row-count half of a line: `+1 row next week`, `−2 rows next week`; nothing for zero. */
export function rowChangeParts(addedRows: number, droppedRows: number): string[] {
  const say = (n: number, sign: string) => `${sign}${n} ${n === 1 ? "row" : "rows"} next week`;
  const out: string[] = [];
  if (addedRows > 0) out.push(say(addedRows, "+"));
  if (droppedRows > 0) out.push(say(droppedRows, "\u2212"));
  return out;
}

const COLUMN: Record<Exclude<FieldDelta["field"], "sets">, "reps" | "weight_kg" | "time_seconds"> = {
  reps: "reps", weightKg: "weight_kg", holdSeconds: "time_seconds",
};

/** The ONE write a Hold makes: one column, this week's value, exactly the rows the row groups (`setFieldWrite.ts`). */
export function holdWrite(d: FieldDelta, row: Pick<WeekRow, "setIds">): SetFieldWrite | null {
  if (!canHold(d, row) || d.field === "sets" || d.from === null) return null;
  return { column: COLUMN[d.field], value: d.from, ids: [...(row.setIds ?? [])] };
}

/** The closed cell's mark: `3 proposals` / `1 proposal`; nothing for none. */
export function proposalsLabel(n: number): string | null {
  if (n <= 0) return null;
  return `${n} ${n === 1 ? "proposal" : "proposals"}`;
}

export type OverlayPair = { latest: string; previous: string; dayKey: string };

/**
 * The overlay's pairs from the client page's own state: each TRAINING assignment after this Sunday
 * against the previous copy of its own template (`nextWeekPairs`, the uniqueness index's identity),
 * carried with the day it falls on so the calendar can put the count on that cell.
 */
export function overlayPairs(
  assignments: readonly { id: string; scheduledDateISO: string | null; isTask: boolean; hiddenFromClient: boolean }[],
  workoutIds: Readonly<Record<string, string>>,
  sources: readonly { id: string; sourceWorkoutId: string | null }[],
  sundayKey: string,
): OverlayPair[] {
  const rootOf = new Map(sources.map((s) => [s.id, s.sourceWorkoutId]));
  const rows = assignments
    .filter((a) => !a.isTask && !a.hiddenFromClient && workoutIds[a.id] !== undefined)
    .map((a) => ({ workoutId: workoutIds[a.id]!, rootId: rootOf.get(a.id) ?? null, scheduledDateISO: a.scheduledDateISO }));
  const dayOf = new Map(rows.map((r) => [r.workoutId, (r.scheduledDateISO ?? "").slice(0, 10)]));
  return nextWeekPairs(rows, sundayKey).map((p) => ({ ...p, dayKey: dayOf.get(p.latest) ?? "" }));
}
